/*
 * <product-load-more>
 * Progressive-enhancement "load more" / infinite scroll for the collection grid.
 *
 * Without JS it is a plain next-page link (SEO-friendly, crawlable, accessible).
 * With JS it fetches the next page via ?section_id=, appends the new product
 * cards to #product-grid and re-points itself at the following page.
 *
 * Modes (data-mode):
 *   - "infinite"  : auto-loads as the user scrolls near the bottom (button still works).
 *   - "load_more" : only loads when the user clicks the button.
 *
 * Plays nicely with facets.js: when filters/sort re-render #ProductGridContainer,
 * a fresh <product-load-more> is parsed from the response and upgraded automatically.
 */
if (!customElements.get('product-load-more')) {
  customElements.define(
    'product-load-more',
    class ProductLoadMore extends HTMLElement {
      constructor() {
        super();
        this.loading = false;
      }

      connectedCallback() {
        this.button = this.querySelector('.load-more__button');
        this.spinner = this.querySelector('.load-more__spinner');
        this.statusEl = document.getElementById('LoadMoreStatus');
        this.mode = this.dataset.mode || 'infinite';

        if (this.button) {
          this.button.addEventListener('click', this.onButtonClick.bind(this));
        }

        // Infinite mode: the "Cargar más" button stays VISIBLE and auto-triggers itself
        // the moment it scrolls into view. So it loads as you reach the button, and if the
        // auto-trigger ever fails the button is right there to tap. The trigger is the
        // button's own position: each load pushes it a full page down, so it naturally
        // loads one page at a time (no runaway).
        if (this.mode === 'infinite' && this.button) {
          if ('IntersectionObserver' in window) {
            this.observer = new IntersectionObserver(
              (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) this.loadMore();
              },
              { rootMargin: '150px 0px' }
            );
            this.observer.observe(this.button);
          }

          // Scroll/resize fallback in case the observer doesn't fire.
          this.onScroll = () => {
            if (this.ticking) return;
            this.ticking = true;
            requestAnimationFrame(() => {
              this.ticking = false;
              this.checkAndLoad();
            });
          };
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onScroll, { passive: true });
        }
      }

      // Auto-load once the button has scrolled into (or near) the viewport.
      checkAndLoad() {
        if (this.loading || !this.nextUrl || !this.button) return;
        const rect = this.button.getBoundingClientRect();
        if (rect.top <= window.innerHeight + 150) this.loadMore();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.onScroll) {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
        }
      }

      onButtonClick(event) {
        event.preventDefault();
        this.loadMore();
      }

      // Returns the <ul> that holds the product cards, handling both layouts:
      //  - collection grid: #product-grid IS the <ul>
      //  - search results:  #product-grid is a wrapper <div>; the <ul> is a descendant
      gridListIn(root) {
        const container = root.getElementById ? root.getElementById('product-grid') : root.querySelector('#product-grid');
        if (!container) return null;
        return container.matches && container.matches('ul') ? container : container.querySelector('ul.product-grid, ul.grid');
      }

      get grid() {
        return this.gridListIn(document);
      }

      get nextUrl() {
        return this.dataset.nextUrl;
      }

      buildFetchUrl(url) {
        const fetchUrl = new URL(url, window.location.origin);
        fetchUrl.searchParams.set('section_id', this.dataset.sectionId);
        return fetchUrl.toString();
      }

      async loadMore() {
        if (this.loading || !this.nextUrl) return;
        this.loading = true;
        this.setBusy(true);

        try {
          const response = await fetch(this.buildFetchUrl(this.nextUrl));
          if (!response.ok) throw new Error('Network response was not ok: ' + response.status);
          const text = await response.text();
          const doc = new DOMParser().parseFromString(text, 'text/html');

          const grid = this.grid;
          const sourceList = this.gridListIn(doc);
          const newItems = sourceList
            ? Array.from(sourceList.children).filter((el) => el.matches && el.matches('li.grid__item'))
            : [];
          if (grid && newItems.length) {
            newItems.forEach((item) => {
              // Cancel the reveal-on-scroll animation so appended cards are visible immediately.
              item.classList.add('scroll-trigger--cancel');
              grid.appendChild(item);
            });
            this.announce(newItems.length);
            this.refreshThirdPartyWidgets();
          }

          // Point at the following page, or finish if this was the last one. We do NOT
          // auto-trigger the next load here; the next page loads only when the user
          // scrolls down again (the observer/scroll listener re-fires naturally). This
          // prevents loading many pages at once.
          const nextComponent = doc.querySelector('product-load-more');
          if (nextComponent && nextComponent.dataset.nextUrl) {
            this.dataset.nextUrl = nextComponent.dataset.nextUrl;
          } else {
            this.finish();
          }
        } catch (error) {
          // The button stays visible, so the user can simply tap it to retry.
          console.error('[product-load-more]', error);
        } finally {
          this.setBusy(false);
          // Brief cooldown so layout/images settle before another auto-load can fire.
          // Hard safety against any tight loop / runaway loading.
          setTimeout(() => {
            this.loading = false;
          }, 200);
        }
      }

      setBusy(busy) {
        if (this.spinner) this.spinner.classList.toggle('hidden', !busy);
        if (this.button) {
          this.button.classList.toggle('load-more__button--loading', busy);
          this.button.setAttribute('aria-busy', busy ? 'true' : 'false');
        }
      }

      announce(count) {
        if (!this.statusEl) return;
        const template = this.dataset.announce || '{{ count }} productos más cargados';
        this.statusEl.textContent = template.replace('{{ count }}', count);
      }

      finish() {
        if (this.statusEl && this.dataset.allLoaded) {
          this.statusEl.textContent = this.dataset.allLoaded;
        }
        if (this.observer) this.observer.disconnect();
        this.remove();
      }

      // Re-initialise review/rating widgets (Loox, etc.) on the freshly added cards.
      refreshThirdPartyWidgets() {
        try {
          if (window.loox && window.loox.widgets && typeof window.loox.widgets.render === 'function') {
            window.loox.widgets.render();
          } else if (window.Loox && typeof window.Loox.reload === 'function') {
            window.Loox.reload();
          }
        } catch (error) {
          /* non-blocking */
        }
        document.dispatchEvent(new CustomEvent('product-grid:appended'));
      }
    }
  );
}
